import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { SubmissionError } from 'redux-form'

import { trackEvent } from '@/core/reducks/analytics'
import { getPosition, patchPosition } from '@/core/apiModules/positions'
import { postPositionAttachment, deletePositionAttachment } from '@/core/apiModules/positionAttachments'
import { trackOpenedClosedPositionInDetails } from '../../helpers/analyticsHelpers'

import { detailsInitialValues } from '../../selectors/positionDetails'
import { currentHiringTeam } from '@/core/selectors/sessions'
import { positionsModule } from '../modules'
import { errorHandler } from '@/core/helpers/errorHelpers'
import { showSystemError, hideSystemError } from '@/core/components/SystemErrorRedux'
import { formatPositionAnalytics } from '@/core/helpers/analyticsHelpers'

export function* fetchDetailSaga({ payload }) {
  yield put(hideSystemError())
  yield put(positionsModule.toggleLoadingDetail(true))

  try {
    const response = yield call(getPosition, payload.hiring_team_id, payload.positionId)
    yield put(positionsModule.fetchDetailSuccess(response))
  } catch (err) {
    const { error, status } = yield call(errorHandler, err)
    yield put(positionsModule.fetchDetailError(error))

    if (status !== 404) {
      yield put(showSystemError(error))
    }
  }

  yield put(positionsModule.toggleLoadingDetail(false))
}

function* updateDetailSaga({ meta }) {
  try {
    const { attachment, ...data } = meta.data
    const { attachment: existingAttachment } = yield select(detailsInitialValues)

    // Only delete if the payload explicitly includes attachment: null
    if (existingAttachment && meta.data.hasOwnProperty('attachment') && attachment === null) {
      yield call(deletePositionAttachment, meta.hiringTeamId, meta.positionId)
    }

    // New attachment added, create it
    if (attachment && !attachment.id) {
      const file = {
        file_name: attachment.name,
        file_url: attachment.url,
      }

      yield call(postPositionAttachment, meta.hiringTeamId, meta.positionId, file)
    }

    const response = yield call(patchPosition, meta.hiringTeamId, meta.positionId, data)
    yield put(positionsModule.updateDetailSuccess(response))
    yield call(meta.resolve)
    const hiringTeam = yield select(currentHiringTeam)
    const formattedPosition = formatPositionAnalytics({
      hiringTeam,
      position: response.result,
      hasJobSpec: !!attachment,
      source: 'positions detail',
      action: 'update position',
    })
    yield put(trackEvent('Company: position action', formattedPosition))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(positionsModule.updateDetailError(error))
    yield call(meta.reject, new SubmissionError({ _error: error }))
  }
}

function* archiveSaga({ meta }) {
  try {
    const response = yield call(patchPosition, meta.hiringTeamId, meta.positionId, meta.data)
    const {
      result: { id: positionId, close_reason: closeReason, close_reason_other_description: otherReason },
    } = response
    yield call(trackOpenedClosedPositionInDetails, { positionId, closeReason, otherReason, action: 'closed' })
    yield put(positionsModule.archiveSuccess(response))
    yield call(meta.resolve)
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(positionsModule.archiveError(error))
    yield call(meta.reject, new SubmissionError({ _error: error }))
  }
}

function* unarchiveSaga({ meta }) {
  try {
    const response = yield call(patchPosition, meta.hiringTeamId, meta.positionId, meta.data)
    const {
      result: { id: positionId },
    } = response
    yield call(trackOpenedClosedPositionInDetails, { positionId, action: 'reopened' })
    yield put(positionsModule.unarchiveSuccess(response))
    yield call(meta.resolve)
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(positionsModule.unarchiveError(error))
    yield call(meta.reject, new SubmissionError({ _error: error }))
  }
}

export default function* watchPositionsSaga() {
  yield takeLatest(positionsModule.FETCH_DETAIL, fetchDetailSaga)
  yield takeEvery(positionsModule.UPDATE_DETAIL, updateDetailSaga)
  yield takeEvery(positionsModule.ARCHIVE, archiveSaga)
  yield takeEvery(positionsModule.UNARCHIVE, unarchiveSaga)
}
